import { z } from 'zod';
import { nullableMoneyMinorStringSchema } from './money';
import { propertySearchSchema, type PropertySearch } from './catalog';
import { AssistantError, type SourceFact } from './assistant';
import { formatExactMoney } from './money';

const priceRangeSchema = z
  .object({
    minimumMinor: nullableMoneyMinorStringSchema,
    maximumMinor: nullableMoneyMinorStringSchema,
    knownCount: z.number().int().nonnegative(),
  })
  .strict();
export const catalogInventorySummarySchema = z
  .object({
    scope: z.literal('matching_inventory'),
    total: z.number().int().nonnegative(),
    pricePen: priceRangeSchema,
    priceUsd: priceRangeSchema,
    minimumAreaM2: z
      .string()
      .regex(/^\d+(?:\.\d+)?$/u)
      .nullable(),
    maximumAreaM2: z
      .string()
      .regex(/^\d+(?:\.\d+)?$/u)
      .nullable(),
    bedroomCounts: z.array(z.number().int().nonnegative()),
    typologies: z.array(z.string()),
  })
  .strict();

export const propertyCatalogItemSchema = z
  .object({
    id: z.uuid(),
    title: z.string().min(1).max(500),
    code: z.string().min(1).max(160),
    pricePenMinor: nullableMoneyMinorStringSchema,
    priceUsdMinor: nullableMoneyMinorStringSchema,
    totalAreaM2: z
      .string()
      .regex(/^\d+(?:\.\d+)?$/u)
      .nullable(),
    viviendaSocial: z.boolean().nullable(),
    availability: z.literal('available'),
    bedroomsCount: z.number().int().nonnegative().nullable().optional(),
    bathroomsCount: z.number().nonnegative().nullable().optional(),
    floorNumber: z.number().int().nullable().optional(),
    typology: z.string().min(1).max(100).nullable().optional(),
  })
  .strict();

export const propertyCatalogResultSchema = z
  .object({
    observedAt: z.iso.datetime({ offset: true }),
    items: z.array(propertyCatalogItemSchema).max(30),
    hasMore: z.boolean(),
    summary: catalogInventorySummarySchema.optional(),
    selectionLinks: z
      .array(z.url({ protocol: /^https$/u }).max(8000))
      .max(5)
      .optional(),
    selectionLinkError: z.literal('SELECTION_LINK_TOO_LONG').optional(),
    selectionInventories: z
      .record(
        z.url({ protocol: /^https$/u }),
        z
          .object({
            unitIds: z.array(z.uuid()).max(30),
            hasMore: z.boolean(),
          })
          .strict()
      )
      .optional(),
    selectionLinkScopes: z
      .record(
        z.url({ protocol: /^https$/u }),
        z.enum(['complete_current_search', 'marketplace_eligible_matches'])
      )
      .optional(),
    ordering: z
      .enum(['price_pen_asc', 'price_pen_desc', 'area_desc', 'floor_asc'])
      .optional(),
    unverifiedCriteria: z.array(z.string().min(1).max(500)).max(20).optional(),
  })
  .strict();
export type PropertyCatalogResult = z.infer<typeof propertyCatalogResultSchema>;

// Publication membership comes from the connector for this exact query and
// authenticated origin. A partial member list cannot prove totals or exclusion.
export function selectPlacementPropertyCatalog(
  catalog: PropertyCatalogResult,
  origin: string | null | undefined
): PropertyCatalogResult {
  if (!origin)
    throw new AssistantError('CATALOG_PLACEMENT_INVENTORY_REQUIRED', 502);
  const inventory = catalog.selectionInventories?.[origin];
  if (!inventory) {
    if (catalog.selectionLinkScopes?.[origin] === 'complete_current_search')
      return catalog;
    throw new AssistantError('CATALOG_PLACEMENT_INVENTORY_REQUIRED', 502);
  }
  const memberIds = new Set(inventory.unitIds);
  if (memberIds.size !== inventory.unitIds.length)
    throw new AssistantError('CATALOG_DUPLICATE_RECORD', 502);
  const items = catalog.items.filter((item) => memberIds.has(item.id));
  const complete = !inventory.hasMore && items.length === memberIds.size;
  const page = { ...catalog };
  delete page.summary;
  if (!complete) return { ...page, items, hasMore: true };
  const prices = (field: 'pricePenMinor' | 'priceUsdMinor') => {
    const known = items
      .flatMap((item) => (item[field] === null ? [] : [item[field]]))
      .sort((left, right) =>
        BigInt(left) < BigInt(right) ? -1 : BigInt(left) > BigInt(right) ? 1 : 0
      );
    const minimum = known[0] ?? null;
    const maximum = known.at(-1) ?? null;
    return {
      minimumMinor: minimum,
      maximumMinor: maximum,
      knownCount: known.length,
    };
  };
  const areas = orderedCatalogItems({ ...page, items }, 'area_desc').flatMap(
    (item) => (item.totalAreaM2 === null ? [] : [item.totalAreaM2])
  );
  return {
    ...page,
    items,
    hasMore: false,
    summary: {
      scope: 'matching_inventory',
      total: items.length,
      pricePen: prices('pricePenMinor'),
      priceUsd: prices('priceUsdMinor'),
      minimumAreaM2: areas.at(-1) ?? null,
      maximumAreaM2: areas[0] ?? null,
      bedroomCounts: [
        ...new Set(
          items.flatMap((item) =>
            item.bedroomsCount == null ? [] : [item.bedroomsCount]
          )
        ),
      ].sort((left, right) => left - right),
      typologies: [
        ...new Set(
          items.flatMap((item) =>
            item.typology == null ? [] : [item.typology]
          )
        ),
      ].sort(),
    },
  };
}

export function summarizePropertyCatalog(catalog: PropertyCatalogResult) {
  return {
    ...(catalog.summary
      ? {
          inventory: {
            ...catalog.summary,
            priceAmounts: Object.fromEntries(
              (['PEN', 'USD'] as const).map((currency) => {
                const range =
                  currency === 'PEN'
                    ? catalog.summary!.pricePen
                    : catalog.summary!.priceUsd;
                return [
                  currency,
                  {
                    minimum:
                      range.minimumMinor === null
                        ? null
                        : formatExactMoney(
                            BigInt(range.minimumMinor),
                            currency,
                            2
                          ),
                    maximum:
                      range.maximumMinor === null
                        ? null
                        : formatExactMoney(
                            BigInt(range.maximumMinor),
                            currency,
                            2
                          ),
                    knownCount: range.knownCount,
                  },
                ];
              })
            ),
          },
        }
      : {}),
    returnedUnitCount: catalog.items.length,
    pricePenRanking: {
      scope: 'returned_results' as const,
      direction: 'ascending' as const,
      completeMatchingInventory:
        !catalog.hasMore &&
        catalog.summary?.total === catalog.items.length &&
        catalog.items.every((item) => item.pricePenMinor !== null),
      units: orderedCatalogItems(catalog, 'price_pen_asc').map(
        (item, index) => ({
          id: item.id,
          code: item.code,
          pricePenMinor: item.pricePenMinor,
          rank: item.pricePenMinor === null ? null : index + 1,
        })
      ),
    },
    ...(catalog.ordering
      ? {
          ordering: catalog.ordering,
          orderingScope: 'returned_results',
          ...(catalog.ordering === 'price_pen_asc' ||
          catalog.ordering === 'price_pen_desc'
            ? { orderingCurrency: 'PEN' }
            : {}),
          orderedUnits: catalog.items.map((item) => ({
            id: item.id,
            code: item.code,
            pricePenMinor: item.pricePenMinor,
            totalAreaM2: item.totalAreaM2,
            floorNumber: item.floorNumber ?? null,
          })),
        }
      : {}),
    allReturnedUnitsMarkedSocialHousing:
      catalog.items.length > 0 &&
      catalog.items.every((item) => item.viviendaSocial === true),
    hasMore: catalog.hasMore,
    unverifiedCriteria: catalog.unverifiedCriteria ?? [],
  };
}

export const catalogConversationSetSchema = z
  .object({
    id: z.string().min(1).max(500),
    query: z.string().min(1).max(16000),
    search: propertySearchSchema.nullable(),
    observedAt: z.iso.datetime({ offset: true }),
    unitIds: z.array(z.uuid()).max(30),
    codes: z.array(z.string()).max(30),
    hasMore: z.boolean(),
  })
  .strict();
export type CatalogConversationSet = z.infer<
  typeof catalogConversationSetSchema
>;

export function catalogConversationSet(
  id: string,
  query: string,
  search: PropertySearch | null,
  catalog: PropertyCatalogResult
): CatalogConversationSet {
  return {
    id,
    query,
    search,
    observedAt: catalog.observedAt,
    unitIds: catalog.items.map((item) => item.id),
    codes: catalog.items.map((item) => item.code),
    hasMore: catalog.hasMore,
  };
}

export function catalogSetFact(set: CatalogConversationSet): SourceFact {
  return {
    id: `catalog-set:${set.id}`,
    source: 'system:conversation-catalog-set',
    version: set.observedAt,
    observedAt: set.observedAt,
    private: false,
    content: JSON.stringify({
      ...set,
      evidenceScope: 'historical_search_membership',
      instruction:
        'These are the units returned for that earlier question. Membership and physical details support follow-up comparisons. Current price and availability require a new read.',
    }),
  };
}

// Each OR branch may be ordered independently upstream. Order their verified union.
export function orderPropertyCatalog(
  catalog: PropertyCatalogResult,
  search: PropertySearch
): PropertyCatalogResult {
  const ordering = search.alternatives[0]!.sort;
  if (
    ordering === 'updated_desc' ||
    search.alternatives.some((item) => item.sort !== ordering)
  )
    return catalog;
  return {
    ...catalog,
    items: orderedCatalogItems(catalog, ordering),
    ordering,
  };
}

function orderedCatalogItems(
  catalog: PropertyCatalogResult,
  ordering: NonNullable<PropertyCatalogResult['ordering']>
): PropertyCatalogResult['items'] {
  const value = (
    item: PropertyCatalogResult['items'][number]
  ): string | null => {
    if (ordering === 'area_desc') return item.totalAreaM2;
    if (ordering === 'floor_asc')
      return item.floorNumber == null ? null : String(item.floorNumber);
    return item.pricePenMinor;
  };
  const descending = ordering === 'price_pen_desc' || ordering === 'area_desc';
  return [...catalog.items].sort((left, right) => {
    const leftValue = value(left);
    const rightValue = value(right);
    if (leftValue === null)
      return rightValue === null ? left.id.localeCompare(right.id) : 1;
    if (rightValue === null) return -1;
    const [leftInteger, leftFraction = ''] = leftValue.split('.');
    const [rightInteger, rightFraction = ''] = rightValue.split('.');
    const scale = Math.max(leftFraction.length, rightFraction.length);
    const exactLeft = BigInt(leftInteger! + leftFraction.padEnd(scale, '0'));
    const exactRight = BigInt(rightInteger! + rightFraction.padEnd(scale, '0'));
    const comparison =
      exactLeft < exactRight ? -1 : exactLeft > exactRight ? 1 : 0;
    return comparison === 0
      ? left.id.localeCompare(right.id)
      : descending
        ? -comparison
        : comparison;
  });
}

export function mergeSourceFacts(
  facts: SourceFact[],
  received: readonly SourceFact[]
): void {
  for (const fact of received) {
    const existing = facts.findIndex((item) => item.id === fact.id);
    if (existing === -1) facts.push(fact);
    else facts[existing] = fact;
  }
}

// Reuse physical comparison details already shown in this conversation. Prices,
// availability and subsidy flags always require a new authoritative catalog read.
export function propertyComparisonFacts(
  catalog: PropertyCatalogResult,
  sources: readonly SourceFact[]
): SourceFact[] {
  return catalog.items.map((item) => {
    const source = sources.find((fact) => fact.id === item.id);
    if (!source)
      throw new AssistantError('CATALOG_COMPARISON_SOURCE_MISSING', 502);
    let physical: Record<string, unknown> = {};
    try {
      const record: unknown = JSON.parse(source.content);
      if (record && typeof record === 'object' && !Array.isArray(record))
        physical = Object.fromEntries(
          [
            'description',
            'amenities',
            'builtAreaM2',
            'parkingSpacesCount',
            'storageUnitsCount',
          ]
            .filter((key) => Object.hasOwn(record, key))
            .map((key) => [key, (record as Record<string, unknown>)[key]])
        );
    } catch {
      // Legacy adapters may supply prose; only their typed physical fields are reusable.
    }
    return {
      ...source,
      content: JSON.stringify({
        ...physical,
        code: item.code,
        title: item.title,
        totalAreaM2: item.totalAreaM2,
        bedroomsCount: item.bedroomsCount ?? null,
        bathroomsCount: item.bathroomsCount ?? null,
        floorNumber: item.floorNumber ?? null,
        typology: item.typology ?? null,
        evidenceScope: 'previously_shown_physical_details',
      }),
    };
  });
}
